/**
 * planTranslate — shared daily-plan / content translation helpers (web).
 *
 * One persisted "content language" per worker (🇳🇴 original / 🇷🇴 / 🇵🇱) drives
 * translation of plan bodies, the home today-plan, and project descriptions /
 * progress notes. The choice is remembered (localStorage + backend prefs) so a
 * worker only taps a flag once — Norwegian project names are never translated.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "DOSERNE_PLAN_LANG";

// value "no" = original (source is Norwegian). Real flag IMAGES (flagcdn) are
// used instead of emoji flags — emoji flags fall back to "NO/RO/PL" text on
// Windows/Chrome. `code` is the ISO country code for the flag image.
// value "" = Original (plan as written, in English → UK flag, no translation).
// no/ro/pl each translate into that language and show ONLY their flag.
export const PLAN_FLAGS = [
  { value: "", code: "gb", label: "Original" },
  { value: "no", code: "no", label: "" },
  { value: "ro", code: "ro", label: "" },
  { value: "pl", code: "pl", label: "" },
];

export const FLAG_LABEL = { no: "Norsk", ro: "Română", pl: "Polski" };
export const flagUrl = (code) => `https://flagcdn.com/w40/${code}.png`;

function initialLang() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved !== null) return saved;
    const menu = localStorage.getItem("DOSERNE_LANG");
    if (menu === "ro") return "ro"; // Romanian menu → default plans to Romanian
  } catch { /* ignore */ }
  return ""; // Original (as written)
}

/** Persisted content-language preference shared across the Doserne app. */
export function usePlanLang(authHeader) {
  const [planLang, setPlanLang] = useState(initialLang);

  const change = useCallback((next) => {
    setPlanLang(next);
    try { localStorage.setItem(LS_KEY, next); } catch { /* ignore */ }
    // Best-effort cross-device sync — never blocks the UI.
    try {
      const h = typeof authHeader === "function" ? authHeader() : authHeader;
      axios.put(`${API}/doserne/prefs`, { plan_lang: next }, h).catch(() => {});
    } catch { /* ignore */ }
  }, [authHeader]);

  return [planLang, change];
}

// In-memory cache so re-renders / tab switches don't re-hit the endpoint.
const _cache = new Map();

/** Translate arbitrary text/HTML to `lang`. Returns original for empty lang. */
export async function translateContent(text, lang, authHeader) {
  if (!text || !text.trim() || !lang) return text;
  const key = `${lang}::${text}`;
  if (_cache.has(key)) return _cache.get(key);
  try {
    const h = typeof authHeader === "function" ? authHeader() : authHeader;
    const { data } = await axios.post(`${API}/doserne/translate`, { text, lang }, h);
    const out = data?.text || text;
    _cache.set(key, out);
    return out;
  } catch {
    return text;
  }
}

/** Auto-translate `text` into the worker's current content language.
 * Returns original until the translation resolves (or if lang = original ""). */
export function useTranslated(text, authHeader) {
  const [planLang] = usePlanLang(authHeader);
  const [out, setOut] = useState(text || "");
  useEffect(() => {
    let alive = true;
    if (!text || !text.trim() || !planLang) { setOut(text || ""); return; }
    translateContent(text, planLang, authHeader).then((t) => { if (alive) setOut(t); });
    return () => { alive = false; };
  }, [text, planLang, authHeader]);
  return out;
}

/** Small flag switcher — real flag images (+ label only for Original). */
export function FlagBar({ value, onChange, className = "" }) {
  return (
    <div className={`inline-flex items-center gap-1.5 flex-wrap ${className}`} data-testid="plan-flag-bar">
      {PLAN_FLAGS.map((f) => {
        const active = (value || "") === f.value;
        return (
          <button
            key={f.value || "orig"}
            onClick={(e) => { e.stopPropagation(); onChange(f.value); }}
            title={f.label || f.code.toUpperCase()}
            aria-label={f.label || f.code}
            className={`inline-flex items-center gap-1.5 ${f.label ? "pl-1 pr-2.5" : "px-1"} py-1 rounded-full border text-[11px] font-bold transition ${active ? "border-slate-900 bg-white text-slate-900 shadow-sm scale-105" : "border-slate-200 bg-white/60 text-slate-500 opacity-70 hover:opacity-100"}`}
            data-testid={`plan-flag-${f.value || "orig"}`}
          >
            <img src={flagUrl(f.code)} alt="" className="w-5 h-[15px] rounded-[3px] object-cover border border-black/10" loading="lazy" />
            {f.label}
          </button>
        );
      })}
    </div>
  );
}
